/**
 * Order view component.
 * @module components/View/OrderView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Confirm,
  Image,
  Input,
  Message,
  Modal,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Icon, Toast } from '@plone/volto/components';
import {
  deleteContent,
  getContent,
  getVocabulary,
  updateContent,
} from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import Api from '@plone/volto/helpers/Api/Api';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import zelleQRImg from './zelle.png';
import pencilSVG from '@plone/volto/icons/pencil.svg';

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${year}-${month}-${day} ${hours}:${minutesStr} ${ampm}`;
};

const parseRecords = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const US_COUNTRY = 'United States';

// The shipping_fee field is stored as {"enabled": boolean, "amount": number}.
const parseShippingFee = (value) => {
  try {
    const parsed = JSON.parse(value);
    return {
      enabled: !!parsed?.enabled,
      amount: Number(parsed?.amount) || 0,
    };
  } catch (error) {
    return { enabled: false, amount: 0 };
  }
};

const getTotal = (record) =>
  record.quantity === undefined || record.price === undefined
    ? 0
    : record.quantity * record.price;

const getSubtotal = (records) =>
  records.reduce((sum, record) => sum + getTotal(record), 0);

const getCreditCardFee = (base) => (Number(base) || 0) * 0.04;

// Each tool's own price already has any EUR markup baked in from when it
// was entered on the order (see OrderToolsWidget), so this only needs to
// show the right currency symbol, not add anything further.
const formatAmount = (amount, currency) => {
  const symbol = currency === 'EUR' ? '€' : '$';
  return `${symbol} ${(Number(amount) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTotal = (record, currency) =>
  formatAmount(getTotal(record), currency);

/**
 * OrderToolRow, a read-only row showing a single ordered tool.
 * Fetches the tool's items itself so it can resolve the size/set label.
 */
const OrderToolRow = ({
  index,
  record,
  toolTitle,
  showInventory,
  currency,
}) => {
  const dispatch = useDispatch();
  const subrequest = `order-tool-${index}`;

  useEffect(() => {
    if (record.tool) {
      dispatch(getContent(`/tools/${record.tool}`, null, subrequest));
    }
  }, [dispatch, record.tool, subrequest]);

  const toolContent = useSelector(
    (state) => state.content.subrequests?.[subrequest]?.data,
  );

  const sizeItem = (toolContent?.items || []).find(
    (item) => item.id === record.size,
  );
  const sizeLabel = sizeItem
    ? `${sizeItem['@type']} ${sizeItem.title || sizeItem.id}`
    : record.size;

  return (
    <TableRow>
      <TableCell>
        <Link to={`/tools/${record.tool}`}>{toolTitle}</Link>
      </TableCell>
      <TableCell>
        {sizeItem ? (
          <Link to={flattenToAppURL(sizeItem['@id'])}>{sizeLabel}</Link>
        ) : (
          sizeLabel
        )}
      </TableCell>
      <TableCell>{record.no}</TableCell>
      <TableCell>{formatAmount(record.price || 0, currency)}</TableCell>
      <TableCell>
        {showInventory
          ? `${record.quantity} (${Number(sizeItem?.inventory) || 0})`
          : record.quantity}
      </TableCell>
      <TableCell>{formatTotal(record, currency)}</TableCell>
    </TableRow>
  );
};

/**
 * Order view component class.
 * @function OrderView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const OrderView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [showCreditCardModal, setShowCreditCardModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'tools' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'payment' }));
  }, [dispatch]);

  useEffect(() => {
    if (content.contact && content.contact !== '000000') {
      dispatch(
        getContent(`/contacts/${content.contact}`, null, 'order-contact'),
      );
    }
  }, [dispatch, content.contact]);

  const contactDetails = useSelector(
    (state) => state.content.subrequests?.['order-contact']?.data,
  );

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );

  const toolsVocabulary = useSelector(
    (state) => state.vocabularies?.tools?.items || [],
  );

  const paymentVocabulary = useSelector(
    (state) => state.vocabularies?.payment?.items || [],
  );

  const tools = parseRecords(content.tools);
  const currency = content.currency;
  const shippingFee = parseShippingFee(content.shipping_fee);

  const subtotalAmount = getSubtotal(tools);
  const shippingAmount = shippingFee.enabled ? shippingFee.amount : 0;
  // Credit card fee is charged on the subtotal AND the shipping fee.
  const creditCardFeeAmount = content.credit_card_fee
    ? getCreditCardFee(subtotalAmount + shippingAmount)
    : 0;
  const showSubtotalRow = content.credit_card_fee || shippingFee.enabled;
  const orderTotal = subtotalAmount + shippingAmount + creditCardFeeAmount;

  const paymentTitle =
    paymentVocabulary.find((item) => item.value === content.payment)?.label ||
    content.payment;

  const contactTitle =
    contactsVocabulary.find((item) => item.value === content.contact)?.label ||
    content.contact;

  const contactSuffix =
    contactDetails?.country === US_COUNTRY
      ? contactDetails?.postal_code
        ? `Zip: ${contactDetails.postal_code}`
        : null
      : contactDetails?.country || null;

  const contactDisplayTitle = contactSuffix
    ? `${contactTitle} (${contactSuffix})`
    : contactTitle;

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  // Moving from Open to an actual payment method (i.e. the order is paid,
  // not cancelled) means the tools it contains are leaving stock for good,
  // so deduct each one's ordered quantity from its own inventory.
  const deductInventory = async () => {
    await Promise.all(
      tools
        .filter((record) => record.tool && record.size)
        .map(async (record) => {
          const path = `/tools/${record.tool}/${record.size}`;
          const sizeContent = await dispatch(getContent(path));
          const inventory = Math.max(
            0,
            (Number(sizeContent?.inventory) || 0) -
              (Number(record.quantity) || 0),
          );
          await dispatch(updateContent(path, { inventory }));
        }),
    );
  };

  const onSetPayment = async (value, extraUpdates = {}) => {
    if (
      content.payment === 'open' &&
      value !== 'open' &&
      value !== 'cancelled'
    ) {
      await deductInventory();
    }
    await dispatch(
      updateContent(flattenToAppURL(content['@id']), {
        payment: value,
        ...extraUpdates,
      }),
    );
    dispatch(getContent(flattenToAppURL(content['@id'])));
  };

  const onSendLink = () =>
    toast.info(
      <Toast title="Not implemented yet" content="Not implemented yet." />,
    );

  // Cancelling a payment (back to Open) means there's no invoice for it yet.
  const onCancelPayment = () => onSetPayment('open', { invoice_date: null });
  const onCancelOrder = () => onSetPayment('cancelled');
  const onReopenOrder = () => onSetPayment('open');

  // The order moves from Open to an actual payment method: that's when it's
  // effectively invoiced.
  const withInvoiceDate = () => ({ invoice_date: new Date().toISOString() });

  const onOpenInvoiceModal = () => {
    const isKnownContact = content.contact && content.contact !== '000000';
    setInvoiceEmail((isKnownContact && contactDetails?.email) || '');
    setInvoiceError(null);
    setShowInvoiceModal(true);
  };

  const onSendInvoice = () => {
    setSendingInvoice(true);
    setInvoiceError(null);
    const api = new Api();
    api
      .post(`${flattenToAppURL(content['@id'])}/@invoice`, {
        data: { email: invoiceEmail },
      })
      .then(() => {
        setSendingInvoice(false);
        setShowInvoiceModal(false);
      })
      .catch((error) => {
        setSendingInvoice(false);
        setInvoiceError(
          error?.response?.body?.message || 'Unable to send invoice.',
        );
      });
  };

  const onZelleDone = async () => {
    setShowZelleModal(false);
    await onSetPayment('zelle', withInvoiceDate());
  };

  // Open the Square Point of Sale app with this order's amount.
  const onOpenSquare = () => {
    const data = {
      amount_money: {
        amount: String(Math.round((Number(orderTotal) || 0) * 100)),
        currency_code: 'USD',
      },
      callback_url: 'https://proseriesleathertools.com/payment',
      client_id: 'sq0idp-7B1mD6Uwm3LbGEi4rIHpRA',
      version: '1.3',
      notes: `Pro Series Tools - Order ${content.id}`,
      options: {
        supported_tender_types: ['CARD_FROM_READER'],
      },
    };
    window.location.href = `square-commerce-v1://payment/create?data=${encodeURIComponent(
      JSON.stringify(data),
    )}`;
  };

  const onCreditCardDone = async () => {
    setShowCreditCardModal(false);
    await onSetPayment('creditcard', withInvoiceDate());
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">Order</div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete order"
        content={`Are you sure you want to delete "${content.title}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <Modal
        size="tiny"
        open={showZelleModal}
        onClose={() => setShowZelleModal(false)}
      >
        <Modal.Header>Pay with Zelle</Modal.Header>
        <Modal.Content image textAlign="center">
          <div>
            <p className="zelle-amount">
              Amount to pay: {formatAmount(orderTotal, currency)}
            </p>
            <Image src={zelleQRImg} alt="Zelle QR code" centered />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            className="rounded"
            type="button"
            onClick={() => setShowZelleModal(false)}
          >
            Cancel
          </Button>
          <Button
            primary
            className="rounded"
            type="button"
            onClick={onZelleDone}
          >
            Done
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        size="tiny"
        open={showCreditCardModal}
        onClose={() => setShowCreditCardModal(false)}
      >
        <Modal.Header>Pay with Credit Card</Modal.Header>
        <Modal.Content>
          <div style={{ textAlign: 'center' }}>
            {!content.credit_card_fee && (
              <Message
                warning
                className="toast-style-message"
                content="The credit card fee is not selected for this order."
              />
            )}
            <p className="zelle-amount">
              Amount to pay: {formatAmount(orderTotal, currency)}
            </p>
            <Button
              primary
              className="rounded"
              type="button"
              onClick={onOpenSquare}
            >
              Open Square
            </Button>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            className="rounded"
            type="button"
            onClick={() => setShowCreditCardModal(false)}
          >
            Cancel
          </Button>
          <Button
            primary
            className="rounded"
            type="button"
            onClick={onCreditCardDone}
          >
            Done
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        size="tiny"
        open={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
      >
        <Modal.Header>Send Invoice</Modal.Header>
        <Modal.Content>
          <Input
            fluid
            className="underline-input"
            type="email"
            placeholder="Email address"
            disabled={sendingInvoice}
            value={invoiceEmail}
            onChange={(e) => setInvoiceEmail(e.target.value)}
          />
          {invoiceError && <Message negative content={invoiceError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button
            className="rounded"
            type="button"
            disabled={sendingInvoice}
            onClick={() => setShowInvoiceModal(false)}
          >
            Cancel
          </Button>
          <Button
            primary
            className="rounded"
            type="button"
            loading={sendingInvoice}
            disabled={sendingInvoice}
            onClick={onSendInvoice}
          >
            OK
          </Button>
        </Modal.Actions>
      </Modal>
      <div className="view-padding">
        <article id="order">
          <h1 className="content-title">Order {content.title}</h1>
          <table className="tables-side-by-side">
            <tbody>
              <tr>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="6"
                          textAlign="center"
                          className="general sub rotated"
                        >
                          <span className="rotate-label">General</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="4">Order #</TableHeaderCell>
                        <TableCell width="8">{content.id}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Contact</TableHeaderCell>
                        <TableCell>
                          <Link to={`/contacts/${content.contact}`}>
                            {contactDisplayTitle}
                          </Link>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Order Date</TableHeaderCell>
                        <TableCell>{formatDate(content.order_date)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Invoice Date</TableHeaderCell>
                        <TableCell>
                          {formatDate(content.invoice_date)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Channel</TableHeaderCell>
                        <TableCell>{content.channel}</TableCell>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </td>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="2"
                          textAlign="center"
                          className="machining sub rotated"
                        >
                          <span className="rotate-label">Payment</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="6">Status</TableHeaderCell>
                        <TableCell width="6">
                          {content.payment === 'cancelled' ? (
                            <span className="label label-danger">
                              Cancelled
                            </span>
                          ) : paymentTitle === 'Open' ? (
                            <span className="label label-info">Open</span>
                          ) : (
                            <span className="label label-success">
                              Paid with {paymentTitle}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                      {content.payment === 'cancelled' ? (
                        <TableRow>
                          <TableCell
                            colspan="2"
                            textAlign="center"
                            className="payment-cancel"
                          >
                            <span className="payment-actions">
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Reopen Order"
                                title="Reopen Order"
                                onClick={onReopenOrder}
                              >
                                Reopen Order
                              </Button>
                            </span>
                          </TableCell>
                        </TableRow>
                      ) : paymentTitle === 'Open' ? (
                        <TableRow>
                          <TableCell>
                            <span className="payment-actions">
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Send Link"
                                title="Send Link"
                                onClick={onSendLink}
                              >
                                Send Link
                              </Button>
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Zelle"
                                title="Zelle"
                                onClick={() => setShowZelleModal(true)}
                              >
                                Zelle
                              </Button>
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Check"
                                title="Check"
                                onClick={() =>
                                  onSetPayment('check', withInvoiceDate())
                                }
                              >
                                Check
                              </Button>
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="payment-actions">
                              <Button
                                primary
                                className="rounded"
                                type="button"
                                aria-label="Credit Card"
                                title="Credit Card"
                                onClick={() => setShowCreditCardModal(true)}
                              >
                                Credit Card
                              </Button>
                              <Button
                                primary
                                className="rounded"
                                type="button"
                                aria-label="Cash"
                                title="Cash"
                                onClick={() =>
                                  onSetPayment('cash', withInvoiceDate())
                                }
                              >
                                Cash
                              </Button>
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Cancel Order"
                                title="Cancel Order"
                                onClick={onCancelOrder}
                              >
                                Cancel Order
                              </Button>
                            </span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell
                            colspan="2"
                            textAlign="center"
                            className="payment-cancel"
                          >
                            <span className="payment-actions">
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Cancel Payment"
                                title="Cancel Payment"
                                onClick={onCancelPayment}
                              >
                                Cancel Payment
                              </Button>
                              <Button
                                className="rounded"
                                type="button"
                                aria-label="Send Invoice"
                                title="Send Invoice"
                                onClick={onOpenInvoiceModal}
                              >
                                Send Invoice
                              </Button>
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow></TableRow>
                      <TableRow textAlign="right"></TableRow>
                    </TableHeader>
                  </Table>
                </td>
              </tr>
            </tbody>
          </table>
          <Table celled>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  rowSpan={
                    tools.length +
                    2 +
                    (showSubtotalRow ? 1 : 0) +
                    (shippingFee.enabled ? 1 : 0) +
                    (content.credit_card_fee ? 1 : 0)
                  }
                  textAlign="center"
                  className="preparation sub rotated"
                >
                  <span className="rotate-label">Tools</span>
                </TableHeaderCell>
                <TableHeaderCell width="4">Tool</TableHeaderCell>
                <TableHeaderCell width="2">Size / Set</TableHeaderCell>
                <TableHeaderCell width="1">No.</TableHeaderCell>
                <TableHeaderCell width="2">Price</TableHeaderCell>
                <TableHeaderCell width="1">
                  {content.payment === 'open' ? 'QTY (INV)' : 'QTY'}
                </TableHeaderCell>
                <TableHeaderCell width="2">Total</TableHeaderCell>
              </TableRow>
              {tools.map((record, index) => (
                <OrderToolRow
                  key={index}
                  index={index}
                  record={record}
                  toolTitle={
                    toolsVocabulary.find(
                      (option) => option.value === record.tool,
                    )?.label || record.tool
                  }
                  showInventory={content.payment === 'open'}
                  currency={currency}
                />
              ))}
              {showSubtotalRow && (
                <TableRow>
                  <TableCell colspan="5">Subtotal</TableCell>
                  <TableCell>
                    {formatAmount(subtotalAmount, currency)}
                  </TableCell>
                </TableRow>
              )}
              {shippingFee.enabled && (
                <TableRow>
                  <TableCell colspan="5">Shipping fee</TableCell>
                  <TableCell>
                    {formatAmount(shippingAmount, currency)}
                  </TableCell>
                </TableRow>
              )}
              {content.credit_card_fee && (
                <TableRow>
                  <TableCell colspan="5">Credit card fee (4%)</TableCell>
                  <TableCell>
                    {formatAmount(creditCardFeeAmount, currency)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow className="grand-total-row">
                <TableCell colspan="5" />
                <TableCell>
                  <strong>{formatAmount(orderTotal, currency)}</strong>
                </TableCell>
              </TableRow>
            </TableHeader>
          </Table>
          {(content.notes || content.attachment) &&
            (() => {
              const extraRows = [
                content.notes && {
                  label: 'Notes',
                  value: content.notes
                    .split('\n')
                    .reduce((all, cur, index) => [
                      ...all,
                      <br key={index} />,
                      cur,
                    ]),
                },
                content.attachment && {
                  label: 'Attachment',
                  value: (
                    <>
                      {content.attachment['content-type']?.startsWith(
                        'image/',
                      ) && (
                        <a
                          href={flattenToAppURL(content.attachment.download)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Image
                            src={flattenToAppURL(content.attachment.download)}
                            alt={content.attachment.filename}
                            style={{ maxWidth: '768px', marginBottom: '8px' }}
                          />
                        </a>
                      )}
                      <a
                        href={flattenToAppURL(content.attachment.download)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {content.attachment.filename}
                      </a>
                    </>
                  ),
                },
              ].filter(Boolean);

              return (
                <Table celled>
                  <TableHeader>
                    {extraRows.map((row, index) => (
                      <TableRow key={row.label}>
                        {index === 0 && (
                          <TableHeaderCell
                            rowSpan={extraRows.length}
                            textAlign="center"
                            className="extra sub rotated"
                          >
                            <span className="rotate-label">Extra</span>
                          </TableHeaderCell>
                        )}
                        <TableHeaderCell width="3">{row.label}</TableHeaderCell>
                        <TableCell width="9">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableHeader>
                </Table>
              );
            })()}
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
OrderView.propTypes = {
  content: PropTypes.shape({
    contact: PropTypes.string,
    order_date: PropTypes.string,
    invoice_date: PropTypes.string,
    channel: PropTypes.string,
    credit_card_fee: PropTypes.bool,
    shipping_fee: PropTypes.string,
    notes: PropTypes.string,
    attachment: PropTypes.shape({
      download: PropTypes.string,
      filename: PropTypes.string,
      'content-type': PropTypes.string,
    }),
  }).isRequired,
};

export default OrderView;
